import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Avatar,
  Tooltip,
  CircularProgress,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInFull as OpenInFullIcon,
  Send as SendIcon,
  AutoAwesome as AutoAwesomeIcon,
  RestartAlt as RestartIcon,
} from '@mui/icons-material';
import { conversationsAPI } from '../services/api';
import { useUI } from '../contexts/UIContext';

interface QuickChatDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface MiniMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  pending?: boolean;
}

const STORAGE_KEY = 'wardrobe.quickChatConversationId';

const STARTERS = [
  "What should I wear today?",
  "Build me a smart-casual look from my wardrobe",
  "What am I missing for spring?",
  "Pack me a 3-day trip outfit set",
];

const QuickChatDrawer: React.FC<QuickChatDrawerProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { toast } = useUI();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<MiniMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 250);
  }, [open]);

  useEffect(() => {
    if (!open || conversationId || bootstrapping) return;
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored) {
      setConversationId(stored);
      void hydrate(stored);
    }
  }, [open, conversationId, bootstrapping]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const hydrate = async (id: string) => {
    try {
      const res = await conversationsAPI.getConversation(id, { include_memory: false, message_limit: 20 });
      const msgs: MiniMessage[] = (res.data.messages || []).map((m: any) => ({
        id: m.id,
        role: m.role,
        content: m.content,
      }));
      setMessages(msgs);
    } catch {
      window.localStorage.removeItem(STORAGE_KEY);
      setConversationId(null);
    }
  };

  const ensureConversation = async (): Promise<string> => {
    if (conversationId) return conversationId;
    setBootstrapping(true);
    try {
      const res = await conversationsAPI.createConversation({
        title: 'Quick Chat',
        conversation_type: 'wardrobe_chat',
      });
      const id = res.data.id;
      window.localStorage.setItem(STORAGE_KEY, id);
      setConversationId(id);
      return id;
    } finally {
      setBootstrapping(false);
    }
  };

  const handleSend = async (text?: string) => {
    const content = (text ?? input).trim();
    if (!content || sending) return;
    setInput('');

    const tempId = `temp-${Date.now()}`;
    setMessages(prev => [
      ...prev,
      { id: tempId, role: 'user', content },
      { id: `${tempId}-pending`, role: 'assistant', content: '', pending: true },
    ]);
    setSending(true);

    try {
      const id = await ensureConversation();
      const res = await conversationsAPI.sendMessage(id, { message: content, include_search: true });
      const data = res.data;
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== `${tempId}-pending`);
        return [
          ...filtered,
          { id: data.assistant_message_id, role: 'assistant', content: data.response },
        ];
      });
    } catch (err: any) {
      setMessages(prev => prev.filter(m => m.id !== `${tempId}-pending`));
      toast(err?.response?.data?.error || 'Could not send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleNewChat = async () => {
    window.localStorage.removeItem(STORAGE_KEY);
    setConversationId(null);
    setMessages([]);
    setInput('');
    inputRef.current?.focus();
  };

  const handleExpand = () => {
    onClose();
    if (conversationId) {
      navigate(`/conversations?id=${conversationId}`);
    } else {
      navigate('/conversations');
    }
  };

  const showStarters = useMemo(() => messages.length === 0 && !sending, [messages.length, sending]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.1 }}>
            AI Stylist
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Powered by your wardrobe
          </Typography>
        </Box>
        <Tooltip title="New chat">
          <span>
            <IconButton size="small" onClick={handleNewChat} disabled={sending}>
              <RestartIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Open full view">
          <IconButton size="small" onClick={handleExpand}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          backgroundColor: 'rgba(0,0,0,0.02)',
        }}
      >
        {showStarters && (
          <Box className="fade-in">
            <Typography variant="overline" color="text.secondary">
              Try asking
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {STARTERS.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  variant="outlined"
                  onClick={() => handleSend(s)}
                  sx={{
                    cursor: 'pointer',
                    height: 'auto',
                    py: 0.75,
                    '& .MuiChip-label': { whiteSpace: 'normal', display: 'block', textTransform: 'none', fontFamily: 'Lato, sans-serif', fontSize: '0.78rem' },
                    '&:hover': { borderColor: 'secondary.main', backgroundColor: 'rgba(212,175,55,0.08)' },
                  }}
                />
              ))}
            </Box>
            <Divider sx={{ my: 3 }} />
            <Typography variant="caption" color="text.secondary">
              Quick Chat keeps a single ongoing thread. Hit the expand icon for the full view, or restart any time.
            </Typography>
          </Box>
        )}

        {messages.map((m) => (
          <Box
            key={m.id}
            className="fade-in"
            sx={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                maxWidth: '85%',
                px: 1.5,
                py: 1,
                backgroundColor: m.role === 'user' ? 'primary.main' : 'background.paper',
                color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                borderLeft: m.role === 'assistant' ? '3px solid' : 'none',
                borderLeftColor: 'secondary.main',
                boxShadow: m.role === 'assistant' ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              {m.pending ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <CircularProgress size={12} thickness={6} />
                  <Typography variant="caption" color="text.secondary">
                    Thinking…
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'Lato, sans-serif' }}>
                  {m.content}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 1.5, borderTop: '1px solid rgba(0,0,0,0.08)', backgroundColor: 'background.paper' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Ask the stylist…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={sending}
          />
          <IconButton
            color="primary"
            onClick={() => handleSend()}
            disabled={!input.trim() || sending}
            sx={{
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: 0,
              '&:hover': { backgroundColor: 'primary.dark' },
              '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.1)' },
            }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default QuickChatDrawer;
